import React, { useMemo, useState } from 'react';

import { NUMBER_FIELD_WARNING, shouldShowNumberFieldWarning } from '@aline/common';
import { Autocomplete, Typography } from '@mui/material';
import { format, isValid, parseISO } from 'date-fns';
import { AlineInput } from 'src/AlineInput';
import { AlineAutocompleteInput } from 'src/components/AlineAutocompleteInput';
import { AlineCheckbox } from 'src/components/generic/AlineCheckbox';
import { AlineDatePicker } from 'src/components/generic/AlineDatePicker';
import { Apollo } from 'src/gql/generated';
import { makeStyles } from 'tss-react/mui';

import { getShouldShowDocumentFieldsSection } from './launch-template-visibility';
import type { OnValuesChange, TemplateQuickStartState } from './LaunchTemplateBody';
import { StepLayout } from '../StepLayout';

const useStyles = makeStyles<{ columns: number }>()((theme, { columns }) => ({
  field: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.5),
  },
  fieldsGrid: {
    display: 'grid',
    gap: theme.spacing(2),
    gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))`,
    width: '100%',
    [theme.breakpoints.down('sm')]: {
      gridTemplateColumns: 'minmax(0, 1fr)',
    },
  },
  group: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
  },
  groupTitle: {
    ...theme.appTypography.fieldLabel,
    color: theme.appColors.iconsType,
  },
  helpText: {
    fontSize: 12,
  },
  label: {
    ...theme.appTypography.fieldLabel,
  },
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(3),
  },
  row: {
    alignItems: 'center',
    display: 'flex',
    gap: theme.spacing(1),
  },
}));

type AlineTemplateField = NonNullable<
  Apollo.LaunchTemplateDataQuery['workflow']
>['intakeFields'][number];
interface TemplateDocument {
  documentId: string;
  name: string;
}

interface LaunchDocumentFieldsStepProps {
  alineFields: AlineTemplateField[];
  documentsList: TemplateDocument[];
  onValuesChange: OnValuesChange;
  quickStartState: TemplateQuickStartState;
}

export function LaunchDocumentFieldsStep({
  alineFields,
  documentsList,
  onValuesChange,
  quickStartState,
}: LaunchDocumentFieldsStepProps) {
  const valueByFieldId = useMemo(() => {
    const map = new Map<string, string>();
    for (const entry of quickStartState.alineFieldValues ?? []) map.set(entry.fieldId, entry.value);
    return map;
  }, [quickStartState.alineFieldValues]);

  if (!getShouldShowDocumentFieldsSection(alineFields.length)) return null;

  return (
    <StepLayout title="Document fields">
      <LaunchDocumentFields
        alineFields={alineFields}
        columns={2}
        documentsList={documentsList}
        valueByFieldId={valueByFieldId}
        onChange={(fieldId, value) =>
          onValuesChange(prev => ({
            alineFieldValues: upsertAlineFieldValue(prev.alineFieldValues ?? [], fieldId, value),
          }))
        }
      />
    </StepLayout>
  );
}

export function LaunchDocumentFields({
  alineFields,
  columns = 1,
  documentsList,
  onChange,
  valueByFieldId,
}: {
  alineFields: AlineTemplateField[];
  documentsList: TemplateDocument[];
  onChange: (fieldId: string, value: string) => void;
  valueByFieldId: Map<string, string>;
  columns?: number;
}) {
  const { classes } = useStyles({ columns });
  const groups = groupFieldsByDocument(alineFields, documentsList);

  return (
    <div className={classes.root}>
      {groups.map(group => (
        <div key={group.key} className={classes.group}>
          {group.title && <Typography className={classes.groupTitle}>{group.title}</Typography>}
          <div className={classes.fieldsGrid}>
            {group.fields.map(field => (
              <LaunchDocumentFieldInput
                key={field.fieldId}
                field={field}
                value={valueByFieldId.get(field.fieldId) ?? ''}
                onChange={value => onChange(field.fieldId, value)}
              />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function LaunchDocumentFieldInput({
  field,
  onChange,
  value,
}: {
  field: AlineTemplateField;
  onChange: (value: string) => void;
  value: string;
}) {
  const { classes } = useStyles({ columns: 1 });
  const helpText = getFieldHelpText(field);
  const helpTextElement = helpText ? (
    <Typography className={classes.helpText} color="textSecondary" variant="body2">
      {helpText}
    </Typography>
  ) : null;

  /** Required fields still unanswered join the launch footer's jump list. */
  if (field.type === Apollo.AlineFieldType.Checkbox) {
    return (
      <div
        className={classes.field}
        data-required-field={field.required && value !== 'true' ? '' : undefined}
      >
        <div className={classes.row}>
          <AlineCheckbox
            checked={value === 'true'}
            onChange={event => onChange(event.target.checked ? 'true' : 'false')}
          />
          <Typography className={classes.label}>{field.label}</Typography>
        </div>
        {helpTextElement}
      </div>
    );
  }

  return (
    <div
      className={classes.field}
      data-required-field={field.required && value === '' ? '' : undefined}
    >
      <Typography className={classes.label}>{field.label}</Typography>
      <LaunchDocumentFieldControl field={field} value={value} onChange={onChange} />
      {helpTextElement}
    </div>
  );
}

function LaunchDocumentFieldControl({
  field,
  onChange,
  value,
}: {
  field: AlineTemplateField;
  onChange: (value: string) => void;
  value: string;
}) {
  const [datePickerOpen, setDatePickerOpen] = useState(false);

  switch (field.type) {
    case Apollo.AlineFieldType.Checkbox:
      return null;
    case Apollo.AlineFieldType.Date: {
      const parsed = value ? parseISO(value) : null;
      const date = parsed && isValid(parsed) ? parsed : null;
      return (
        <AlineDatePicker
          enableFlip={true}
          open={datePickerOpen}
          setOpen={setDatePickerOpen}
          value={date}
          onDateChange={next => onChange(next && isValid(next) ? format(next, 'yyyy-MM-dd') : '')}
        />
      );
    }
    case Apollo.AlineFieldType.Number:
      return (
        <AlineInput
          helperText={NUMBER_FIELD_WARNING}
          hideHelperText={!shouldShowNumberFieldWarning(value)}
          placeholder="Enter number..."
          value={value}
          onChange={event => onChange(event.target.value)}
        />
      );
    case Apollo.AlineFieldType.Picklist: {
      const options = field.picklistOptions ?? [];
      const selected = options.find(option => option.value === value) ?? null;
      return (
        <Autocomplete
          getOptionLabel={option => option.label}
          isOptionEqualToValue={(a, b) => a.value === b.value}
          options={options}
          renderInput={params => <AlineAutocompleteInput {...params} />}
          value={selected}
          onChange={(_event, option) => onChange(option?.value ?? '')}
        />
      );
    }
    case Apollo.AlineFieldType.Text:
      return (
        <AlineInput fullWidth value={value} onChange={event => onChange(event.target.value)} />
      );
  }
}

/**
 * The variable's question doubles as the label when no label is set, so only
 * surface it as helper text when it adds information beyond the label.
 */
function getFieldHelpText(field: AlineTemplateField): string | null {
  const question = field.question?.trim();
  if (!question || question === field.label) return null;
  return question;
}

function groupFieldsByDocument(fields: AlineTemplateField[], documents: TemplateDocument[]) {
  const nameById = new Map(documents.map(document => [document.documentId, document.name]));
  const uniqueDocIds = new Set(fields.flatMap(field => field.usedInDocuments));
  if (uniqueDocIds.size <= 1) return [{ fields, key: 'all', title: null }];

  const shared: AlineTemplateField[] = [];
  const byDoc = new Map<string, AlineTemplateField[]>();
  for (const field of fields) {
    if (field.usedInDocuments.length !== 1) {
      shared.push(field);
      continue;
    }
    const documentId = field.usedInDocuments[0];
    const existing = byDoc.get(documentId) ?? [];
    existing.push(field);
    byDoc.set(documentId, existing);
  }

  const groups = [...byDoc.entries()].map(([documentId, docFields]) => ({
    fields: docFields,
    key: documentId,
    title: nameById.get(documentId) ?? 'Document',
  }));
  if (shared.length) groups.push({ fields: shared, key: 'shared', title: 'Shared fields' });
  return groups;
}

function upsertAlineFieldValue(
  current: Apollo.AlineFieldValueInput[],
  fieldId: string,
  value: string
): Apollo.AlineFieldValueInput[] {
  const without = current.filter(entry => entry.fieldId !== fieldId);
  if (value === '') return without;
  return [...without, { fieldId, value }];
}
